import Api from './api';
import moment from 'moment';
import { trimSlashes, __, getProp } from './utilities';
import { NotFoundError } from './errors';

import {
  fetchSectionPath,
  fetchArticles,
  fetchSectionArticles,
  fetchSections
} from './csm.service.js';

export const ACTION = {
  SET_ALERT: 'SET_ALERT',
  LOADING_ADD: 'LOADING_ADD',
  LOADING_REMOVE: 'LOADING_REMOVE'
};

// Alert
export const setAlert = (config = null) => {
  return {
    type: ACTION.SET_ALERT,
    config: config
  };
};
export const successAlert = (title, config = {}) => {
  return dispatch => {
    dispatch(
      setAlert(
        Object.assign(
          {
            type: 'success',
            title: title,
            onConfirm: () => dispatch(setAlert(null))
          },
          config
        )
      )
    );
  };
};
export const errorAlert = (title, config = {}) => {
  return dispatch => {
    dispatch(
      setAlert(
        Object.assign(
          {
            type: 'error',
            title: title,
            onConfirm: () => dispatch(setAlert(null))
          },
          config
        )
      )
    );
  };
};
export const warningAlert = (title, config = {}) => {
  return dispatch => {
    dispatch(
      setAlert(
        Object.assign(
          {
            type: 'warning',
            title: title,
            onConfirm: () => dispatch(setAlert(null))
          },
          config
        )
      )
    );
  };
};
export const confirmAlert = (title, config = {}) => {
  return dispatch => {
    dispatch(
      setAlert(
        Object.assign(
          {
            type: 'warning',
            title: title,
            showCancelButton: true,
            confirmButtonText: __('Áno'),
            cancelButtonText: __('Zrušiť'),
            onConfirm: () => dispatch(setAlert(null)),
            onCancel: () => dispatch(setAlert(null))
          },
          config
        )
      )
    );
  };
};

// Loading related
export const addLoading = () => {
  return {
    type: ACTION.LOADING_ADD
  };
};
export const removeLoading = () => {
  return {
    type: ACTION.LOADING_REMOVE
  };
};

// XHR related
export const xhrRequest = key => {
  return {
    type: `${key}_REQUEST`
  };
};
export const xhrRequestSuccess = (key, data) => {
  return {
    type: `${key}_SUCCESS`,
    data,
    receivedAt: moment().toISOString()
  };
};
export const xhrRequestFailure = (key, httpStatus = null) => {
  return {
    type: `${key}_FAILURE`,
    httpStatus
  };
};

// CSM related
export const getSection = (cspace, sectionId) => {
  const key = 'SECTION';
  return (dispatch, getStore) => {
    const sectionsById = getStore().sections.byId;
    if (sectionId in sectionsById) {
      return Promise.resolve();
    }
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return Api.get(`/cm/public/${cspace}/sections/${sectionId}`)
      .then(response => {
        return fetchSectionPath(cspace, getProp(response, 'section._id')).then(
          path => {
            let sectionArticles = [];
            fetchSections(cspace, response.section._id).then(
              subsectionResponse => {
                sectionArticles = subsectionResponse.sections.map(
                  subsection => {
                    return fetchSectionArticles(cspace, subsection._id, {
                      limit: 4
                    });
                  }
                );
                Promise.all(sectionArticles).then(articlesResponse => {
                  const subsections = subsectionResponse.sections.map(
                    (subsection, i) => {
                      return Object.assign({}, subsection, {
                        articles: articlesResponse[i].articles
                      });
                    }
                  );
                  dispatch(removeLoading());
                  dispatch(
                    xhrRequestSuccess(key, {
                      id: response.section._id,
                      url: response.section.url,
                      response: Object.assign(response, {
                        path: path.path,
                        subsections
                      })
                    })
                  );
                });
              }
            );
          }
        );
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key, getProp(error, 'details.code', null)));
        throw new NotFoundError();
      });
  };
};
export const getArticle = (cspace, id) => {
  const key = 'ARTICLE';
  return (dispatch, getStore) => {
    const articlesById = getStore().articles.byId;
    if (id in articlesById) {
      return Promise.resolve();
    }
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return Api.get(`/cm/public/${cspace}/articles/${id}`)
      .then(response => {
        let sectionId = getProp(response, 'sectionid', null);
        let pathPromise = Promise.resolve([]);
        if (sectionId) {
          pathPromise = fetchSectionPath(cspace, sectionId);
        }
        return pathPromise.then(path => {
          dispatch(
            xhrRequestSuccess(key, {
              id,
              response: {
                content: response,
                path: path.path
              }
            })
          );
          dispatch(removeLoading());
        });
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key, getProp(error, 'details.code', null)));
        throw new NotFoundError();
      });
  };
};
export const getSections = (cspace, key, sectionId) => {
  return dispatch => {
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return fetchSections(cspace, sectionId)
      .then(response => {
        dispatch(removeLoading());
        dispatch(xhrRequestSuccess(key, response));
        return response;
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key));
        throw new NotFoundError();
      });
  };
};
export const getArticles = (cspace, key, extraParams = {}) => {
  return dispatch => {
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return fetchArticles(cspace, extraParams)
      .then(response => {
        dispatch(removeLoading());
        dispatch(xhrRequestSuccess(key, response));
        return response;
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key));
        throw new NotFoundError();
      });
  };
};
export const getSectionArticles = (
  cspace,
  key,
  sectionId,
  extraParams = {}
) => {
  return dispatch => {
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return fetchSectionArticles(cspace, sectionId, extraParams)
      .then(response => {
        dispatch(removeLoading());
        dispatch(xhrRequestSuccess(key, response));
        return response;
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key));
        throw error;
      });
  };
};
export const fetchStaticText = (cspace, key, textId) => {
  return dispatch => {
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return Api.get(`/cm/public/${cspace}/static-content/${textId}`)
      .then(response => {
        dispatch(removeLoading());
        dispatch(xhrRequestSuccess(key, response));
        return response;
      })
      .catch(error => {
        dispatch(removeLoading());
        dispatch(xhrRequestFailure(key));
        throw error;
      });
  };
};
export const fetchFooterStaticText = (cspace, textId) => {
  return dispatch => {
    dispatch(fetchStaticText(cspace, 'FOOTER_STATIC_TEXT', textId));
  };
};
export const fetchContentItemByUrl = (cspace, url, config = {}) => {
  return (dispatch, getStore) => {
    const contentItemByUrl = getStore().contentItems.byUrl;
    if (url in contentItemByUrl) {
      return Promise.resolve();
    }
    const key = 'CONTENT_ITEM';
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    return new Promise((resolve, reject) => {
      Api.get(`/cm/public/${cspace}/urlmap/${trimSlashes(url)}?expand_object=1`, {}, config)
        .then(response => {
          if (response.urltype === 'section') {
            return fetchSectionPath(cspace, response.object_id)
              .then(path => {
                let sectionArticles = [];
                fetchSections(cspace, response.object_id).then(
                  subsectionResponse => {
                    sectionArticles = subsectionResponse.sections.map(
                      subsection => {
                        return fetchSectionArticles(cspace, subsection._id, {
                          limit: 4
                        });
                      }
                    );
                    Promise.all(sectionArticles).then(articlesResponse => {
                      const subsections = subsectionResponse.sections.map(
                        (subsection, i) => {
                          return Object.assign({}, subsection, {
                            articles: articlesResponse[i].articles
                          });
                        }
                      );
                      dispatch(
                        xhrRequestSuccess('SECTION', {
                          id: response.object_id,
                          url: url,
                          response: Object.assign(response.expanded_object, {
                            path: path.path,
                            subsections
                          })
                        })
                      );
                      dispatch({
                        type: `${key}_SUCCESS`,
                        data: response,
                        url
                      });
                      dispatch(removeLoading());
                      resolve();
                    });
                  }
                );
              })
              .catch(() => {
                throw new NotFoundError();
              });
          } else if (response.urltype === 'article') {
            let sectionId = getProp(
              response,
              'expanded_object.sectionid',
              null
            );
            let pathPromise = Promise.resolve([]);
            if (sectionId) {
              pathPromise = fetchSectionPath(cspace, sectionId);
            }
            return pathPromise
              .then(path => {
                dispatch(
                  xhrRequestSuccess('ARTICLE', {
                    id: response.object_id,
                    url: url,
                    response: {
                      content: response.expanded_object,
                      path: path.path
                    }
                  })
                );
                dispatch({
                  type: `${key}_SUCCESS`,
                  data: response,
                  url
                });
                dispatch(removeLoading());
                resolve();
              })
              .catch(() => {
                throw new NotFoundError();
              });
          }
        })
        .catch(error => {
          dispatch(removeLoading());
          dispatch(
            xhrRequestFailure(key, getProp(error, 'response.status', null))
          );

          reject();
        });
    });
  };
};

export const fetchFeeds = () => {
  const key = 'FEEDS';
  return dispatch => {
    dispatch(xhrRequest(key));
    dispatch(addLoading());
    Api.get('/cm/public/feed', {
      limit: 10,
    }).then(response => {
      dispatch(removeLoading())
      dispatch(xhrRequestSuccess(key, response))
    }).catch(error => {
      dispatch(removeLoading());
      dispatch(xhrRequestFailure(key, getProp(error, 'response.status', null)));
    })
  }
}

export const getAuthorArticles = (cspace, author, limit, offset, loadMore = false) => {
  const key = 'AUTHOR_ARTICLES';
  return async (dispatch) => {
    try {
      dispatch(xhrRequest(key));
      const data = await Api.get(`/cm/public/${cspace}/articles/author/${author}`, {limit, offset})
      dispatch(xhrRequestSuccess(key, {apiData: data, loadMore}));
    } catch(e) {
      dispatch(xhrRequestFailure(key, getProp(e, 'response.status', null)))
    }
  }
}



// WEBPACK FOOTER //
// ./src/ui/src/actions.js